import {Radio as WeaveRadio} from '@wandb/weave/components';
import * as React from 'react';

import {useRampFlagGorillaOutliersAvailable} from './../../../util/rampFeatureFlags';

/**
 * Types of point visualization:
 * BucketingGorilla: this is new functionality using gorilla to bucket points without sampling. We ejected from Weave to do this for time, but long-term hope to move this to weave.

 * Sampling: this is the legacy functionality of doing sampling/bucketing clientside
 */
export const POINT_VISUALIZATION_OPTIONS = {
  BucketingGorilla: 'bucketing-gorilla',
  Sampling: 'sampling',
} as const;

export type PointVisualizationOptions =
  (typeof POINT_VISUALIZATION_OPTIONS)[keyof typeof POINT_VISUALIZATION_OPTIONS];

type PointVisualizationProps = {
  checkGorillaOutliers?: typeof useRampFlagGorillaOutliersAvailable;
  onValueChange: (value: PointVisualizationOptions) => void;
  value?: PointVisualizationOptions;
};

export const PointVisualization = React.memo(
  ({
    checkGorillaOutliers = useRampFlagGorillaOutliersAvailable,
    onValueChange,
    value,
  }: PointVisualizationProps) => {
    const hasGorillaSampling = checkGorillaOutliers();

    if (!hasGorillaSampling) {
      return null;
    }
    return (
      <fieldset className="mt-16 text-moon-800">
        <div className="text-base font-semibold leading-8">
          Point visualization method
        </div>
        <WeaveRadio.Root
          aria-label="Number of runs shown in panels"
          name="tooltip-runs"
          onValueChange={onValueChange}
          value={value ?? POINT_VISUALIZATION_OPTIONS.Sampling}>
          {hasGorillaSampling && (
            <div className="mt-12 flex items-start justify-start">
              <WeaveRadio.Item
                id="point-visualization-bucketing-gorilla"
                value={POINT_VISUALIZATION_OPTIONS.BucketingGorilla}>
                <WeaveRadio.Indicator />
              </WeaveRadio.Item>
              <label
                className="ml-12"
                htmlFor="point-visualization-bucketing-gorilla">
                <div className="mb-6">Bucketing</div>
                <div className="text-sm text-moon-500">
                  Groups and averages nearby points. Extreme values are shaded.
                </div>
              </label>
            </div>
          )}

          <div className="mt-12 flex items-start justify-start">
            <WeaveRadio.Item
              id="point-visualization-sampling"
              value={POINT_VISUALIZATION_OPTIONS.Sampling}>
              <WeaveRadio.Indicator />
            </WeaveRadio.Item>
            <label className="ml-12" htmlFor="point-visualization-sampling">
              <div className="mb-6">Downsampling</div>
              <div className="text-sm text-moon-500">
                Randomly downsamples run points. Outliers may be excluded.
              </div>
            </label>
          </div>
        </WeaveRadio.Root>
      </fieldset>
    );
  }
);
PointVisualization.displayName = 'PointVisualization';
